import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Eye, ExternalLink, History, RefreshCcw } from 'lucide-react';
import { DeploymentRecord } from '../../types/deployment';
import {
  formatDate,
  getDeploymentActivityAt,
  getRepositoryName,
  statusColor,
} from './deploymentExplorerUtils';

export type DeploymentHistoryState = {
  items: DeploymentRecord[];
  total: number;
  loading: boolean;
  loaded: boolean;
  error: string;
};

interface DeploymentHistoryPanelProps {
  historyState: DeploymentHistoryState | undefined;
  onLoadMore: () => void;
  onOpenDeploymentDetails: (deploymentId: string) => void;
  onRefreshHistory: () => void;
  saving?: boolean;
}

const renderGitLabLink = (deploymentRecord: DeploymentRecord) => {
  if (!deploymentRecord.gitlab?.pipelineWebUrl && !deploymentRecord.gitlab?.jobWebUrl) {
    return null;
  }

  const href = deploymentRecord.gitlab.jobWebUrl || deploymentRecord.gitlab.pipelineWebUrl || '';
  const label = deploymentRecord.gitlab.jobName
    || (deploymentRecord.gitlab.pipelineId ? `Pipeline #${deploymentRecord.gitlab.pipelineId}` : 'Open GitLab');

  return (
    <Typography
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="caption"
      color="primary"
      sx={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
    >
      {label}
      <ExternalLink size={12} />
    </Typography>
  );
};

export const DeploymentHistoryPanel: React.FC<DeploymentHistoryPanelProps> = ({
  historyState,
  onLoadMore,
  onOpenDeploymentDetails,
  onRefreshHistory,
  saving = false,
}) => {
  return (
    <Card
      variant="outlined"
      sx={{
        mt: 1.5,
        ml: { md: 4 },
        bgcolor: 'rgba(255,255,255,0.02)',
      }}
    >
      <Box sx={{ px: 2, py: 1.5 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <History size={16} />
            <Typography variant="subtitle2">Superseded Deployments</Typography>
            {historyState?.loaded && (
              <Chip
                size="small"
                variant="outlined"
                label={`${historyState.items.length}/${historyState.total}`}
              />
            )}
          </Stack>
          <Button
            size="small"
            variant="text"
            startIcon={historyState?.loading ? <CircularProgress size={14} /> : <RefreshCcw size={14} />}
            disabled={historyState?.loading || saving}
            onClick={onRefreshHistory}
          >
            Refresh
          </Button>
        </Stack>
      </Box>
      <Divider />
      <Box sx={{ px: 2, py: 1.5 }}>
        {historyState?.error && (
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            {historyState.error}
          </Alert>
        )}

        {historyState?.loading && !historyState.loaded ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 3 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : null}

        {historyState?.loaded && historyState.items.length === 0 && !historyState.loading ? (
          <Typography variant="body2" color="text.secondary">
            No superseded deployments were found for this deployment slice yet.
          </Typography>
        ) : null}

        <Stack spacing={1.25}>
          {(historyState?.items || []).map((deploymentRecord) => (
            <Card key={deploymentRecord._id} variant="outlined" sx={{ px: 1.5, py: 1.25 }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                spacing={1.5}
                alignItems={{ md: 'center' }}
              >
                <Stack spacing={0.5}>
                  <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                    <Typography variant="body2" fontWeight={600}>
                      {getRepositoryName(deploymentRecord.repository)}
                    </Typography>
                    <Chip size="small" variant="outlined" label={deploymentRecord.environment || '-'} />
                    <Chip size="small" label={deploymentRecord.actionType} />
                    <Chip
                      size="small"
                      color={statusColor(deploymentRecord.status)}
                      label={deploymentRecord.status}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {deploymentRecord.requestedRelease?.displayVersion || 'No version recorded'} • Updated {formatDate(getDeploymentActivityAt(deploymentRecord))}
                  </Typography>
                  {renderGitLabLink(deploymentRecord)}
                </Stack>
                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                  <Tooltip title="View deployment details">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => onOpenDeploymentDetails(deploymentRecord._id)}
                      >
                        <Eye size={16} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              </Stack>
            </Card>
          ))}
        </Stack>

        {historyState && historyState.items.length < historyState.total ? (
          <Box sx={{ mt: 1.5 }}>
            <Button
              size="small"
              variant="outlined"
              onClick={onLoadMore}
              disabled={historyState.loading || saving}
            >
              Load More History
            </Button>
          </Box>
        ) : null}
      </Box>
    </Card>
  );
};
